import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { listProfiles } from '@/features/profiles/lib/profileQueries';
import { ProfileAvatar } from '@/features/profiles/components/profile-avatar';
import { CaremoryAiLoader } from '@/components/caremory-ai-loader';
import { ImagePrepError, prepareImage } from '@/features/timeline/lib/imagePrep';
import { uploadPrescriptionImage } from '@/features/timeline/lib/upload';
import {
  extractErrorMessage,
  extractPrescription,
  type ExtractErrorCode,
} from '@/features/timeline/lib/extract';
import { newEventId } from '@/features/timeline/lib/eventId';
import type { Profile } from '@/types/db';
import type { ReviewRouteState } from './review';

type PipelineStep = 'prep' | 'upload' | 'read' | 'done';

type Stage =
  | { kind: 'loading' }
  | { kind: 'pick_profile'; profiles: Profile[] }
  | { kind: 'pick_photo'; profile: Profile }
  | { kind: 'uploading'; profile: Profile; step: PipelineStep }
  | {
      kind: 'error';
      profile: Profile | null;
      message: string;
      allowRetry: boolean;
    };

export function AddPage() {
  const params = useParams<{ id?: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const presetProfileId = params.id ?? search.get('profile') ?? null;
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    listProfiles()
      .then((ps) => {
        if (cancelled) return;
        setProfiles(ps);
      })
      .catch((err) => {
        console.error('[add] listProfiles', err);
        if (cancelled) return;
        setStage({
          kind: 'error',
          profile: null,
          message: "Couldn't load profiles.",
          allowRetry: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!profiles) return;
    if (profiles.length === 0) {
      setStage({
        kind: 'error',
        profile: null,
        message: 'Add a family member before adding records.',
        allowRetry: false,
      });
      return;
    }
    if (presetProfileId) {
      const profile = profiles.find((p) => p.id === presetProfileId);
      if (profile) {
        setStage({ kind: 'pick_photo', profile });
        return;
      }
    }
    if (profiles.length === 1) {
      setStage({ kind: 'pick_photo', profile: profiles[0] });
      return;
    }
    setStage({ kind: 'pick_profile', profiles });
  }, [profiles, presetProfileId]);

  async function handlePick(profile: Profile, file: File) {
    const eventId = newEventId();
    try {
      setStage({ kind: 'uploading', profile, step: 'prep' });
      const image = await prepareImage(file);

      setStage({ kind: 'uploading', profile, step: 'upload' });
      const attachment = await uploadPrescriptionImage(
        profile.id,
        eventId,
        image,
      );

      setStage({ kind: 'uploading', profile, step: 'read' });
      const result = await extractPrescription({
        storagePath: attachment.storage_path,
        profileId: profile.id,
      });

      if (!result.success) {
        handleExtractFailure(profile, eventId, attachment, result.error);
        return;
      }

      setStage({ kind: 'uploading', profile, step: 'done' });
      const reviewState: ReviewRouteState = {
        profile,
        eventId,
        attachment,
        envelope: result.envelope,
        extractError: null,
      };
      navigate('/review', { state: reviewState });
    } catch (err) {
      console.error('[add] pipeline failed', err);
      if (err instanceof ImagePrepError) {
        setStage({
          kind: 'error',
          profile,
          message: err.message,
          allowRetry: true,
        });
        return;
      }
      setStage({
        kind: 'error',
        profile,
        message: "Something went wrong. You can try again.",
        allowRetry: true,
      });
    }
  }

  function handleExtractFailure(
    profile: Profile,
    eventId: string,
    attachment: Awaited<ReturnType<typeof uploadPrescriptionImage>>,
    code: ExtractErrorCode,
  ) {
    if (code === 'unauthorized') {
      navigate('/login', { replace: true, state: { from: '/' } });
      return;
    }
    if (code === 'forbidden') {
      setStage({
        kind: 'error',
        profile,
        message: extractErrorMessage(code),
        allowRetry: false,
      });
      return;
    }
    // Photo is uploaded; let the user type manually on the Review screen.
    const reviewState: ReviewRouteState = {
      profile,
      eventId,
      attachment,
      envelope: null,
      extractError: code,
    };
    navigate('/review', { state: reviewState });
  }

  const backLink = useMemo(() => {
    if (presetProfileId) return `/profile/${presetProfileId}`;
    return '/';
  }, [presetProfileId]);

  return (
    <main className="min-h-screen safe-top safe-bottom">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-2">
          <Link
            to={backLink}
            aria-label="Back"
            className="h-11 w-11 -ml-1 rounded-full flex items-center justify-center text-ink-muted hover:text-ink active:bg-canvas shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>
          <h1 className="text-lg font-bold truncate flex-1">Add record</h1>
        </div>
      </header>

      <section className="px-4 py-6 max-w-md mx-auto">
        {stage.kind === 'loading' && (
          <div className="flex justify-center py-16" aria-busy="true">
            <CaremoryAiLoader size="md" label="Loading profiles…" />
          </div>
        )}

        {stage.kind === 'pick_profile' && (
          <>
            <p className="text-sm text-ink-muted mb-3">Who is this record for?</p>
            <ul className="space-y-2">
              {stage.profiles.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setStage({ kind: 'pick_photo', profile: p })}
                    className="card w-full flex items-center gap-3 p-4 min-h-tap text-left active:bg-canvas"
                  >
                    <ProfileAvatar name={p.name} color={p.avatar_color} />
                    <span className="font-medium">{p.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {stage.kind === 'pick_photo' && (
          <UploadPickerCard
            profile={stage.profile}
            onPick={(file) => handlePick(stage.profile, file)}
          />
        )}

        {stage.kind === 'uploading' && (
          <div className="card p-6">
            <div className="flex justify-center mb-4">
              <CaremoryAiLoader
                size="md"
                label={
                  stage.step === 'prep'
                    ? 'Getting the photo ready…'
                    : stage.step === 'upload'
                      ? 'Uploading securely…'
                      : 'Caremory is reading your prescription…'
                }
              />
            </div>
            <p className="text-xs text-ink-subtle text-center mb-5">
              Usually takes 5–10 seconds. Don't close the app.
            </p>
            <PipelineStepper step={stage.step} />
          </div>
        )}

        {stage.kind === 'error' && (
          <div className="card p-6 text-center">
            <p className="text-flag-block font-medium mb-2">Something went wrong</p>
            <p className="text-sm text-ink-muted mb-4">{stage.message}</p>
            {stage.allowRetry && stage.profile && (() => {
              const retryProfile = stage.profile;
              return (
                <button
                  type="button"
                  onClick={() =>
                    setStage({ kind: 'pick_photo', profile: retryProfile })
                  }
                  className="btn-primary w-full"
                >
                  Try again
                </button>
              );
            })()}
            {!stage.allowRetry && (
              <Link to="/home" className="btn-secondary inline-flex">
                Back to home
              </Link>
            )}
          </div>
        )}
      </section>
    </main>
  );
}

function UploadPickerCard({
  profile,
  onPick,
}: {
  profile: Profile;
  onPick: (file: File) => void;
}) {
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onPick(file);
  }

  return (
    <>
      <input
        ref={galleryRef}
        type="file"
        accept="image/png,image/jpeg,image/heic,image/heif,application/pdf,.heic,.heif,.pdf"
        hidden
        onChange={handleChange}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={handleChange}
      />

      <div className="card p-5">
        <div className="flex items-center gap-3 mb-5">
          <ProfileAvatar name={profile.name} color={profile.avatar_color} />
          <div className="min-w-0">
            <p className="text-xs text-ink-muted">For</p>
            <p className="font-bold truncate">{profile.name}</p>
          </div>
        </div>

        {/* Clickable upload zone */}
        <button
          type="button"
          onClick={() => galleryRef.current?.click()}
          className="w-full rounded-xl border-2 border-dashed border-accent/40 bg-accent-soft/20 hover:bg-accent-soft/40 active:bg-accent-soft/60 transition-colors p-5 flex flex-col items-center text-center mb-4"
          aria-label="Upload prescription, lab report, or scan"
        >
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft mb-3">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6 text-accent"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </span>
          <p className="font-semibold text-ink mb-1">Drop a photo or tap to upload</p>
          <p className="text-sm text-ink-muted mb-2">
            Have a document? Drop a prescription, lab report, or scan.
          </p>
          <p className="text-xs text-ink-subtle">PNG · JPEG · HEIC · PDF</p>
        </button>

        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          className="btn-primary w-full inline-flex items-center justify-center gap-2"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 shrink-0"
            aria-hidden="true"
          >
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          Take a photo
        </button>
      </div>

      <p className="text-sm text-ink-muted text-center mt-4">
        Logging a visit, vaccine, vitals, or scan without a photo?{' '}
        <Link
          to={`/profile/${profile.id}/manual`}
          className="text-accent font-medium"
        >
          Enter it manually
        </Link>
      </p>
    </>
  );
}

const PIPELINE_STEPS: { key: PipelineStep; label: string; help: string }[] = [
  { key: 'prep', label: 'Preparing photo', help: 'Rotating and compressing.' },
  { key: 'upload', label: 'Uploading', help: 'Encrypted in transit.' },
  { key: 'read', label: 'Reading your document', help: 'Claude is looking at the photo.' },
];

function PipelineStepper({ step }: { step: PipelineStep }) {
  const currentIdx = PIPELINE_STEPS.findIndex((s) => s.key === step);
  return (
    <ol className="relative space-y-3" aria-label="Processing steps">
      <span
        aria-hidden="true"
        className="absolute left-[11px] top-3 bottom-3 w-px bg-border"
      />
      {PIPELINE_STEPS.map((s, idx) => {
        const done = step === 'done' || idx < currentIdx;
        const active = !done && idx === currentIdx;
        return (
          <li key={s.key} className="relative flex items-start gap-3" aria-current={active ? 'step' : undefined}>
            <span
              aria-hidden="true"
              className={clsx(
                'relative shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold border-2',
                done && 'border-accent bg-accent text-white',
                active && 'border-accent bg-surface text-accent animate-pulse-ring',
                !done && !active && 'border-border bg-surface text-ink-muted',
              )}
            >
              {done ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-3 w-3"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                idx + 1
              )}
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <p
                className={clsx(
                  'text-sm font-medium',
                  active ? 'text-accent' : done ? 'text-ink' : 'text-ink-muted',
                )}
              >
                {s.label}
                {/* Dots animate while active; once done they become solid red */}
                {active && (
                  <span className="inline-flex items-center gap-0.5 ml-2 align-middle">
                    <span
                      className={clsx('h-1.5 w-1.5 rounded-full bg-accent', active && 'motion-safe:animate-typing-dot')}
                      style={active ? { animationDelay: '0ms' } : undefined}
                    />
                    <span
                      className={clsx('h-1.5 w-1.5 rounded-full bg-accent', active && 'motion-safe:animate-typing-dot')}
                      style={active ? { animationDelay: '150ms' } : undefined}
                    />
                    <span
                      className={clsx('h-1.5 w-1.5 rounded-full bg-accent', active && 'motion-safe:animate-typing-dot')}
                      style={active ? { animationDelay: '300ms' } : undefined}
                    />
                  </span>
                )}
              </p>
              <p className="text-xs text-ink-muted">{s.help}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
